import {
  useState,
  useCallback,
} from 'react';

import { useHistory } from 'react-router-dom';

import {
  successLog,
} from '@n3/browser-messages';

import {
  buttonColors,
} from '../Button';
import {
  showConfirm,
  ConfirmError,
} from '../showConfirm';

import { processError } from './processError';
import type {
  UseRemoveButtonParams,
  UseRemoveButtonResult,
} from './types';

export const useRemoveButton = (
  {
    remove,
    onRemoveSuccess,
    approveTitle,
    approveContent,
    approveButtonText,
    approveButtonColor = buttonColors.DANGER,
    cancelButtonText,
    errorsPath = 'response.data.detail',
    redirectTo,
    notification,
  }: UseRemoveButtonParams,
): UseRemoveButtonResult => {
  const history = useHistory();
  const [isRemoving, setIsRemoving] = useState(false);

  const onClickAsync = useCallback(async () => {
    if (isRemoving) {
      return;
    }

    setIsRemoving(true);

    const confirmResult = await showConfirm({
      title: approveTitle,
      content: approveContent,
      approveButtonText,
      approveButtonColor,
      cancelButtonText,

      approve: async () => {
        let hasError: boolean;
        let error: unknown;

        try {
          await remove();
          hasError = false;
        } catch (e) {
          error = e;
          hasError = true;
        }

        if (hasError) {
          if (error instanceof ConfirmError) {
            throw error;
          }

          throw new ConfirmError(processError(error, errorsPath));
        }
      },
    });

    setIsRemoving(false);

    if (confirmResult) {
      if (notification) {
        successLog({
          isAutoClose: true,
          ...notification,
        });
      }

      if (onRemoveSuccess) {
        onRemoveSuccess();
      }

      if (redirectTo) {
        history.push(redirectTo);
      }
    }
  }, [
    approveButtonText,
    approveButtonColor,
    approveContent,
    approveTitle,
    cancelButtonText,
    errorsPath,
    history,
    isRemoving,
    notification,
    onRemoveSuccess,
    redirectTo,
    remove,
  ]);

  const onClick = useCallback(() => {
    onClickAsync()
      .catch((e) => {
        throw e;
      });
  }, [onClickAsync]);

  return {
    isRemoving,
    onClickAsync,
    onClick,
  };
};
